import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Header } from "@/components/Header";
import { ProfileSidebarAnfitriao } from "@/components/ProfileSidebarAnfitriao";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import FullCalendar from "@fullcalendar/react";
import type { DateSelectArg, EventClickArg, EventInput } from "@fullcalendar/core";
import type { DateClickArg } from "@fullcalendar/interaction";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import CalculadoraTaxaModal from "@/components/CalculadoraTaxaModal";

declare global {
  interface Window {
    __AUTH_ID__?: number | null;
    __AUTH_USER__?: {
      id?: number;
      name?: string;
      email?: string;
      role?: string;
      has_ong?: boolean;
      ong_panel_url?: string;
      avatar_url?: string | null;
      avatarUrl?: string | null;
      profile_photo_url?: string | null;
      foto_url?: string | null;
      foto?: string | null;
      avatar?: string | null;
    } | null;
  }
}

type TipoPrecificacao = "horaria" | "diaria" | "creche" | string;

interface Servico {
  id: number | string;
  nome: string;
  tipo_precificacao: TipoPrecificacao;
  tipo_servico_id?: number | string;
  preco_base?: number | string | null;
  preco_banho?: number | string | null;
  preco_tosa?: number | string | null;
  preco_banho_tosa?: number | string | null;
}

interface FormDataAgenda {
  id: string;
  data: string;
  horaInicio: string;
  horaFim: string;
  preco: string;
  precoBanho: string;
  precoTosa: string;
  precoBanhoTosa: string;
  indisponivel: boolean;
}

interface ReservaDetalhe {
  id: string;
  title: string;
  tutor: string;
  tutorId?: number;
  telefoneTutor: string;
  mensagem: string;
  status: string;
  dataInicio?: string;
  dataFim?: string;
  horaInicio?: string;
  horaFim?: string;
  valorLiquido?: string;
  chatUrl?: string;
  checkinRealizadoEm?: string | null;
  checkoutRealizadoEm?: string | null;
  pets: Array<any>;
}

const FORM_PADRAO: FormDataAgenda = {
  id: "",
  data: "",
  horaInicio: "08:00",
  horaFim: "09:00",
  preco: "",
  precoBanho: "",
  precoTosa: "",
  precoBanhoTosa: "",
  indisponivel: false,
};

function getCsrfToken(): string {
  const el = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null;
  return el?.content ?? "";
}

function valorParaCampo(valor: number | string | null | undefined): string {
  if (valor === null || valor === undefined || valor === "") return "";
  const numero = Number(String(valor).replace(",", "."));
  if (!Number.isFinite(numero) || numero <= 0) return "";
  return numero.toFixed(2).replace(".", ",");
}

function parseMoeda(valor: string): number | null {
  const limpo = String(valor ?? "").trim();
  if (!limpo) return null;
  const numero = Number(limpo.replace(/\./g, "").replace(",", "."));
  return Number.isFinite(numero) && numero > 0 ? numero : null;
}

// FIX 2: Extrai hora de um objeto Date local (evita problemas de timezone com strings ISO)
function extrairHoraDeDate(date: Date): string {
  const h = String(date.getHours()).padStart(2, "0");
  const m = String(date.getMinutes()).padStart(2, "0");
  return `${h}:${m}`;
}

// FIX 2: Extrai data local (YYYY-MM-DD) de um objeto Date (evita offset de timezone)
function extrairDataDeDate(date: Date): string {
  const ano = date.getFullYear();
  const mes = String(date.getMonth() + 1).padStart(2, "0");
  const dia = String(date.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
}

function getHojeInicio() {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  return hoje;
}

function getHojeStr() {
  return extrairDataDeDate(new Date());
}

function getHoraAtualStr() {
  return extrairHoraDeDate(new Date());
}

export default function AnfitriaoAgenda() {
  const [servicos, setServicos] = useState<Servico[]>([]);
  const [servicoId, setServicoId] = useState<string>("");
  const [eventos, setEventos] = useState<EventInput[]>([]);

  const [loading, setLoading] = useState(true);
  const [loadingEventos, setLoadingEventos] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [avisoAgenda, setAvisoAgenda] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState<FormDataAgenda>(FORM_PADRAO);

  const [calculadoraAberta, setCalculadoraAberta] = useState(false);
  const [calculadoraLoteAberta, setCalculadoraLoteAberta] = useState(false);
  
  const [modalReservaOpen, setModalReservaOpen] = useState(false);
  const [reservaSelecionada, setReservaSelecionada] = useState<ReservaDetalhe | null>(null);

  const [acaoReservaCarregando, setAcaoReservaCarregando] = useState<
    "aceitar" | "recusar" | "checkin" | "checkout" | null
  >(null);

  const primeiroCampoModalRef = useRef<HTMLInputElement | null>(null);
  const [petExpandido, setPetExpandido] = useState<number | null>(null);

  const [carregandoReserva, setCarregandoReserva] = useState(false);

  const [modalLoteOpen, setModalLoteOpen] = useState(false);
  const [salvandoLote, setSalvandoLote] = useState(false);
  const [loteDataInicio, setLoteDataInicio] = useState("");
  const [loteDataFim, setLoteDataFim] = useState("");
  const [loteHoraInicio, setLoteHoraInicio] = useState("08:00");
  const [loteHoraFim, setLoteHoraFim] = useState("09:00");
  const [lotePreco, setLotePreco] = useState("");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!modalOpen) return;
    const t = window.setTimeout(() => {
      primeiroCampoModalRef.current?.focus();
    }, 100);
    return () => window.clearTimeout(t);
  }, [modalOpen]);

  useEffect(() => {
    if (!avisoAgenda) return;
    const t = window.setTimeout(() => setAvisoAgenda(""), 2500);
    return () => window.clearTimeout(t);
  }, [avisoAgenda]);

  const servicoAtual = useMemo(
    () => servicos.find((s) => String(s.id) === servicoId),
    [servicos, servicoId]
  );

  const ehHorario =
    servicoAtual?.tipo_precificacao === "horaria" ||
    servicoAtual?.tipo_precificacao === "hora";

  const ehBanhoTosa =
    Number(servicoAtual?.tipo_servico_id) === 8 ||
    String(servicoAtual?.nome ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim() === "banho e tosa";

  const hojeStr = getHojeStr();

  const precosBanhoTosaAtuais = useCallback(
    () => ({
      precoBanho: valorParaCampo(servicoAtual?.preco_banho ?? 60),
      precoTosa: valorParaCampo(servicoAtual?.preco_tosa ?? 90),
      precoBanhoTosa: valorParaCampo(servicoAtual?.preco_banho_tosa ?? 130),
    }),
    [servicoAtual]
  );

  const podeSelecionarIntervalo = useCallback(
    (start: Date) => {
      const agora = new Date();
      if (ehHorario) {
        return start >= agora;
      }
      return start >= getHojeInicio();
    },
    [ehHorario]
  );

  const fetchServicos = useCallback(async () => {
    try {
      setLoading(true);
      setErro("");

      const resp = await fetch("/agenda/servicos", {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (!resp.ok) throw new Error("Erro ao carregar serviços");

      const data = await resp.json();
      const lista = Array.isArray(data) ? data : (data.servicos ?? []);
      const servicoIdDaUrl = new URLSearchParams(window.location.search).get("servico_id");

      setServicos(lista);

      if (lista.length > 0) {
        setServicoId((atual) => {
          if (atual) return atual;

          if (servicoIdDaUrl && lista.some((item: Servico) => String(item.id) === servicoIdDaUrl)) {
            return servicoIdDaUrl;
          }

          return String(lista[0].id);
        });
      }
    } catch {
      setErro("Não foi possível carregar os serviços.");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEventos = useCallback(async () => {
    if (!servicoId) return;

    try {
      setLoadingEventos(true);
      setErro("");

      const resp = await fetch(`/anfitriao/agenda/dados?servico_id=${servicoId}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });

      if (!resp.ok) throw new Error("Erro ao carregar eventos");

      const data = await resp.json();
      const lista = Array.isArray(data) ? data : (data.eventos ?? []);

      const eventosNormais: EventInput[] = lista.map((item: any) => ({
        ...item,
        allDay: item.allDay ?? !(item.start ?? "").includes("T"),
        extendedProps: {
          ...item.extendedProps,
          tipo: item.id?.toString().startsWith("reserva")
            ? "reserva"
            : "disponibilidade",
        },
      }));

      const agora = new Date();
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const hojeFormatado = extrairDataDeDate(hoje);
      const horaAtual = `${String(agora.getHours()).padStart(2, "0")}:${String(
        agora.getMinutes()
      ).padStart(2, "0")}:00`;

      const eventosPassado: EventInput[] = ehHorario
        ? [
            {
              start: `${hojeFormatado}T05:00:00`,
              end: `${hojeFormatado}T${horaAtual}`,
              display: "background",
              classNames: ["bg-passado-hoje"],
            },
          ]
        : [
            {
              start: "2000-01-01",
              end: hojeFormatado,
              display: "background",
              classNames: ["bg-passado-dia"],
            },
          ];

      // FIX 1: Não filtrar eventos de background — eles precisam ser incluídos para mostrar
      // a cor cinza nos horários/dias passados. O eventClick já trata o clique neles.
      setEventos([...eventosNormais, ...eventosPassado]);
    } catch {
      setErro("Erro ao atualizar calendário.");
    } finally {
      setLoadingEventos(false);
    }
  }, [servicoId, ehHorario]);

  useEffect(() => {
    fetchServicos();
  }, [fetchServicos]);

  useEffect(() => {
    fetchEventos();
  }, [fetchEventos]);

  // FIX 2: Usa objetos Date para extrair data e hora locais, evitando problemas de timezone
  const handleSelect = (info: DateSelectArg) => {
    if (!podeSelecionarIntervalo(info.start)) {
      setAvisoAgenda("Não é possível selecionar datas ou horários passados.");
      return;
    }

    const data = extrairDataDeDate(info.start);

    if (ehHorario) {
      const inicio = extrairHoraDeDate(info.start);
      const fimDate = new Date(info.start.getTime() + 60 * 60000);
      const fim = extrairHoraDeDate(fimDate);

      setFormData({
        ...FORM_PADRAO,
        ...precosBanhoTosaAtuais(),
        data,
        horaInicio: inicio,
        horaFim: fim,
      });
    } else {
      setFormData({
        ...FORM_PADRAO,
        ...precosBanhoTosaAtuais(),
        data,
        horaInicio: "08:00",
        horaFim: "09:00",
      });
    }

    setModalOpen(true);
  };

   // FIX 3: Usa objetos Date para extrair data e hora locais no clique de evento
  const handleEventClick = async (info: EventClickArg) => {
    const ev = info.event;

    // ignora blocos cinza de passado
    if (ev.display === "background") {
      setAvisoAgenda("Datas e horários passados ficam bloqueados apenas para edição.");
      return;
    }

    const agora = new Date();
    const inicioEvento = ev.start ? new Date(ev.start) : null;
    const ehPassado = inicioEvento ? inicioEvento.getTime() < agora.getTime() : false;

    // reserva: pode abrir até se for passada
    if (ev.extendedProps?.tipo === "reserva" || String(ev.id).startsWith("reserva-")) {
      try {
        setCarregandoReserva(true);

        const reservaIdBruto =
          ev.extendedProps?.reservaId ??
          ev.extendedProps?.reserva_id ??
          String(ev.id).replace("reserva-", "");

        const reservaId = String(reservaIdBruto).replace("reserva-", "");

        const resp = await fetch(`/reserva/${reservaId}/detalhes-json`, {
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          credentials: "same-origin",
        });

        if (!resp.ok) {
          throw new Error(`Erro ao buscar detalhes da reserva (${resp.status})`);
        }

        const data = await resp.json();

        setReservaSelecionada({
          id: reservaId,
          title: ev.title,
          tutor: data.tutor ?? ev.extendedProps?.tutor ?? "",
          tutorId: data.tutor_id ?? ev.extendedProps?.tutorId ?? undefined,
          telefoneTutor: data.telefone_tutor ?? "",
          status: data.status ?? ev.extendedProps?.status ?? "",
          dataInicio: data.data_inicio ?? ev.extendedProps?.dataInicio ?? "",
          dataFim: data.data_fim ?? ev.extendedProps?.dataFim ?? "",
          horaInicio: data.hora_inicio ?? ev.extendedProps?.horaInicio ?? "",
          horaFim: data.hora_fim ?? ev.extendedProps?.horaFim ?? "",
          checkinRealizadoEm: data.checkin_realizado_em ?? null,
          checkoutRealizadoEm: data.checkout_realizado_em ?? null,
          valorLiquido:
            data.valor_liquido !== null &&
            data.valor_liquido !== undefined &&
            !(typeof data.valor_liquido === "number" && Number.isNaN(data.valor_liquido))
              ? data.valor_liquido
              : ev.extendedProps?.valorLiquido !== null &&
                  ev.extendedProps?.valorLiquido !== undefined &&
                  !(typeof ev.extendedProps?.valorLiquido === "number" &&
                    Number.isNaN(ev.extendedProps?.valorLiquido))
                ? ev.extendedProps?.valorLiquido
                : ev.extendedProps?.valor_liquido !== null &&
                    ev.extendedProps?.valor_liquido !== undefined &&
                    !(typeof ev.extendedProps?.valor_liquido === "number" &&
                      Number.isNaN(ev.extendedProps?.valor_liquido))
                  ? ev.extendedProps?.valor_liquido
                  : "",
          mensagem: data.mensagem ?? "",
          pets: Array.isArray(data.pets) ? data.pets : [],
          chatUrl: data.chat_url ?? null,
        });

        setModalReservaOpen(true);
        setAvisoAgenda(ehPassado ? "Visualizando reserva do histórico." : "");
      } catch (error) {
        console.error(error);
        setAvisoAgenda("Não foi possível carregar os detalhes da reserva.");
      } finally {
        setCarregandoReserva(false);
      }

      return;
    }

    // disponibilidade passada continua bloqueada
    if (ehPassado) {
      setAvisoAgenda("Não é possível editar datas ou horários passados.");
      return;
    }

    // edição normal de disponibilidade
    const start = ev.start;
    const end = ev.end;

    setFormData({
      id: String(ev.id ?? ""),
      data: start ? extrairDataDeDate(start) : "",
      horaInicio: start ? extrairHoraDeDate(start) : "08:00",
      horaFim: end ? extrairHoraDeDate(end) : ehHorario ? "09:00" : "09:00",
      preco:
        ev.extendedProps?.preco !== null &&
        ev.extendedProps?.preco !== undefined &&
        ev.extendedProps?.preco !== ""
          ? String(ev.extendedProps.preco)
          : "",
      ...precosBanhoTosaAtuais(),
      indisponivel: ev.extendedProps?.status === "indisponivel",
    });

    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!servicoId || !formData.data) {
      alert("Dados insuficientes");
      return;
    }

    if (ehHorario) {
      const agora = new Date();
      const inicioSelecionado = new Date(`${formData.data}T${formData.horaInicio}`);
      const fimSelecionado = new Date(`${formData.data}T${formData.horaFim}`);

      if (inicioSelecionado < agora) {
        alert("Não é permitido salvar horário passado.");
        return;
      }

      if (fimSelecionado <= inicioSelecionado) {
        alert("O horário final deve ser maior que o horário inicial.");
        return;
      }
    }

    if (ehBanhoTosa) {
      const precoBanho = parseMoeda(formData.precoBanho);
      const precoTosa = parseMoeda(formData.precoTosa);
      const precoBanhoTosa = parseMoeda(formData.precoBanhoTosa);

      if (!precoBanho || !precoTosa || !precoBanhoTosa) {
        alert("Informe os preços de Banho, Tosa e Banho e Tosa.");
        return;
      }
    }

    try {
      setSalvando(true);

      const isEdicao = !!formData.id && !formData.id.startsWith("reserva");
      const url = `${window.location.origin}/anfitriao/disponibilidade${
        isEdicao ? `/${formData.id}` : ""
      }`;

      const payload: any = {
        servico_id: servicoId,
        data: formData.data,
        preco: formData.indisponivel || ehBanhoTosa ? null : parseMoeda(formData.preco),
          indisponivel: formData.indisponivel ? 1 : 0,
        tipo_precificacao: servicoAtual?.tipo_precificacao,
        _method: isEdicao ? "PUT" : undefined,
      };

      if (ehBanhoTosa) {
        payload.preco_banho = parseMoeda(formData.precoBanho);
        payload.preco_tosa = parseMoeda(formData.precoTosa);
        payload.preco_banho_tosa = parseMoeda(formData.precoBanhoTosa);
      }

      // Adicionar campos específicos para serviço horário
      if (ehHorario) {
        payload.hora_inicio = formData.horaInicio;
        payload.hora_fim = formData.horaFim;
      }

      // Remover _method se for undefined
      if (payload._method === undefined) {
        delete payload._method;
      }

      const resp = await fetch(url, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN": getCsrfToken(),
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });

      if (!resp.ok) {
        const errorData = await resp.json().catch(() => ({}));
        console.error("Erro do servidor:", errorData);
        throw new Error(errorData.message || "Erro ao salvar");
      }

      setModalOpen(false);
      await fetchServicos();
      await fetchEventos();
    } catch (error) {
      console.error("Erro no handleSave:", error);
      alert(error instanceof Error ? error.message : "Erro ao salvar.");
    } finally {
      setSalvando(false);
    }
  };

  const handleDelete = async () => {
    if (!formData.id) return;
    if (!window.confirm("Excluir esta disponibilidade?")) return;

    try {
      const resp = await fetch(`/anfitriao/disponibilidade/${formData.id}`, {
        method: "DELETE",
        credentials: "same-origin",
        headers: {
          "X-CSRF-TOKEN": getCsrfToken(),
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      if (!resp.ok) throw new Error("Erro ao excluir");

      setModalOpen(false);
      await fetchEventos();
    } catch {
      alert("Erro ao excluir.");
    }
  };

  const aceitarReserva = async (id: string) => {
    if (acaoReservaCarregando) return;

    try {
      setAcaoReservaCarregando("aceitar");

      const reservaId = extrairIdReserva(id);

      const resp = await fetch(`/anfitriao/pagamentos/${reservaId}/capturar`, {
        method: "POST",
        headers: {
          "X-CSRF-TOKEN": getCsrfToken(),
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!resp.ok) {
        throw new Error("Erro ao aceitar reserva.");
      }

      setReservaSelecionada((atual) =>
        atual && extrairIdReserva(atual.id) === reservaId
          ? { ...atual, status: "confirmada" }
          : atual
      );
      await fetchEventos();

    } catch (error) {
      alert("Erro ao aceitar reserva");
    } finally {
      setAcaoReservaCarregando(null);
    }
  };


  const recusarReserva = async (id: string) => {
    if (acaoReservaCarregando) return;

    try {
      setAcaoReservaCarregando("recusar");

      const reservaId = extrairIdReserva(id);

      const resp = await fetch(`/anfitriao/pagamentos/${reservaId}/recusar`, {
        method: "POST",
        headers: {
          "X-CSRF-TOKEN": getCsrfToken(),
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      if (!resp.ok) {
        throw new Error("Erro ao recusar reserva.");
      }

      await fetchEventos();
      setModalReservaOpen(false);
      setReservaSelecionada(null);
      setPetExpandido(null);

    } catch (error) {
      alert("Erro ao recusar reserva");
    } finally {
      setAcaoReservaCarregando(null);
    }
  };

  function extrairIdReserva(id: string) {
    return id.startsWith("reserva-") ? id.replace("reserva-", "") : id;
  }

  const realizarCheckin = async (id: string) => {
    if (acaoReservaCarregando) return;

    try {
      setAcaoReservaCarregando("checkin");

      const reservaId = extrairIdReserva(id);
      const resp = await fetch(`/anfitriao/reservas/${reservaId}/check-in`, {
        method: "POST",
        headers: {
          "X-CSRF-TOKEN": getCsrfToken(),
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      const data = await resp.json().catch(() => null);

      if (!resp.ok || data?.ok === false) {
        throw new Error(data?.message || "Erro ao realizar check-in.");
      }

      setReservaSelecionada((atual) =>
        atual && extrairIdReserva(atual.id) === reservaId
          ? {
              ...atual,
              checkinRealizadoEm:
                data?.checkin_realizado_em ||
                data?.reserva?.checkin_realizado_em ||
                new Date().toISOString(),
            }
          : atual
      );
      await fetchEventos();
    } catch (error) {
      alert(error instanceof Error ? error.message : "Erro ao realizar check-in");
    } finally {
      setAcaoReservaCarregando(null);
    }
  };

  const realizarCheckout = async (id: string) => {
    if (acaoReservaCarregando) return;

    try {
      setAcaoReservaCarregando("checkout");

      const reservaId = extrairIdReserva(id);
      const resp = await fetch(`/anfitriao/reservas/${reservaId}/check-out`, {
        method: "POST",
        headers: {
          "X-CSRF-TOKEN": getCsrfToken(),
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
      });

      const data = await resp.json().catch(() => null);

      if (!resp.ok || data?.ok === false) {
        throw new Error(data?.message || "Erro ao realizar check-out.");
      }

      setReservaSelecionada((atual) =>
        atual && extrairIdReserva(atual.id) === reservaId
          ? {
              ...atual,
              status: "concluida",
              checkoutRealizadoEm:
                data?.checkout_realizado_em ||
                data?.reserva?.checkout_realizado_em ||
                new Date().toISOString(),
            }
          : atual
      );
      await fetchEventos();
    } catch (error) {
      alert(error instanceof Error ? error.message : "Erro ao realizar check-out");
    } finally {
      setAcaoReservaCarregando(null);
    }
  };

  function dataValida(data?: string | null) {
  if (!data || data === "0000-00-00") return false;
  return !Number.isNaN(new Date(`${String(data).slice(0, 10)}T00:00:00`).getTime());
}

function calcularResumoSaudePet(pet: any) {
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);

  function chaveVacina(v: any) {
    if (v.vacina_id) return `id:${v.vacina_id}`;

    const nomeCustom =
      typeof v.vacina_nome_custom === "string"
        ? v.vacina_nome_custom.trim().toLowerCase()
        : "";

    if (nomeCustom) return `custom:${nomeCustom}`;

    const nome =
      typeof v.nome === "string" ? v.nome.trim().toLowerCase() : "";

    if (nome) return `nome:${nome}`;

    return null;
  }

  function chaveParasita(p: any) {
    const tipo = typeof p.tipo === "string" ? p.tipo.trim().toLowerCase() : "";
    if (!tipo) return null;
    return tipo;
  }

  const vacinasOriginais = Array.isArray(pet?.vacinas) ? pet.vacinas : [];
  const parasitasOriginais = Array.isArray(pet?.parasitas) ? pet.parasitas : [];

  const vacinasMap = new Map<string, any>();

  vacinasOriginais.forEach((v: any) => {
    const chave = chaveVacina(v);
    if (!chave || !dataValida(v.data_revacinar)) return;

    const atualMs = new Date(`${String(v.data_revacinar).slice(0, 10)}T00:00:00`).getTime();
    const existente = vacinasMap.get(chave);

    if (!existente) {
      vacinasMap.set(chave, v);
      return;
    }

    const existenteMs = new Date(
      `${String(existente.data_revacinar).slice(0, 10)}T00:00:00`
    ).getTime();

    if (atualMs > existenteMs) {
      vacinasMap.set(chave, v);
    }
  });

  const parasitasMap = new Map<string, any>();

  parasitasOriginais.forEach((p: any) => {
    const chave = chaveParasita(p);
    if (!chave || !dataValida(p.data_reaplicar)) return;

    const atualMs = new Date(`${String(p.data_reaplicar).slice(0, 10)}T00:00:00`).getTime();
    const existente = parasitasMap.get(chave);

    if (!existente) {
      parasitasMap.set(chave, p);
      return;
    }

    const existenteMs = new Date(
      `${String(existente.data_reaplicar).slice(0, 10)}T00:00:00`
    ).getTime();

    if (atualMs > existenteMs) {
      parasitasMap.set(chave, p);
    }
  });

  const vacinas = Array.from(vacinasMap.values());
  const parasitas = Array.from(parasitasMap.values());

  let vacAtrasadas = 0;
  let vacProximas = 0;
  let vacEmDia = 0;

  vacinas.forEach((v: any) => {
    const data = new Date(`${String(v.data_revacinar).slice(0, 10)}T00:00:00`);
    const diff = Math.floor((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

    if (data < hoje) vacAtrasadas++;
    else if (diff <= 30) vacProximas++;
    else vacEmDia++;
  });

  let parAtrasados = 0;
  let parProximos = 0;
  let parEmDia = 0;

  parasitas.forEach((p: any) => {
    const data = new Date(`${String(p.data_reaplicar).slice(0, 10)}T00:00:00`);
    const diff = Math.floor((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

    if (data < hoje) parAtrasados++;
    else if (diff <= 30) parProximos++;
    else parEmDia++;
  });

  const total = vacinas.length + parasitas.length;

  let statusGeral = "Sem dados";
  if (total > 0) {
    if (vacAtrasadas > 0 || parAtrasados > 0) statusGeral = "Atrasadas";
    else if (vacProximas > 0 || parProximos > 0) statusGeral = "Próximas";
    else statusGeral = "Em dia";
  }

  return {
    statusGeral,
    vacinas: {
      atrasadas: vacAtrasadas,
      proximas: vacProximas,
      emDia: vacEmDia,
    },
    parasitas: {
      atrasados: parAtrasados,
      proximos: parProximos,
      emDia: parEmDia,
    },
  };
}

  const formatarHoraSimples = (hora?: string | null) => {
    if (!hora) return "—";
    return hora.slice(0, 5);
  };

  const parsePrecoBR = (valor: string): number => {
    if (!valor) return 0;
    const normalizado = String(valor)
      .replace(/\./g, "")
      .replace(",", ".");
    const n = parseFloat(normalizado);
    return Number.isFinite(n) ? n : 0;
  };

  const formatarTelefoneBR = (telefone?: string | null) => {
    if (!telefone) return "Não informado";

    const numeros = telefone.replace(/\D/g, "");

    if (numeros.length === 13 && numeros.startsWith("55")) {
      return `+55 (${numeros.slice(2, 4)}) ${numeros.slice(4, 9)}-${numeros.slice(9, 13)}`;
    }

    if (numeros.length === 11) {
      return `(${numeros.slice(0, 2)}) ${numeros.slice(2, 7)}-${numeros.slice(7, 11)}`;
    }

    return telefone;
  };

  const formatarPeso = (peso?: string | number | null) => {
    if (peso === null || peso === undefined || peso === "") return "—";
    return `${String(peso).replace(/\.00$/, "")} kg`;
  };

  const textoSimNao = (valor: unknown) => {
    if (
      valor === true ||
      valor === 1 ||
      valor === "1" ||
      valor === "sim" ||
      valor === "Sim"
    ) {
      return "Sim";
    }

    if (
      valor === false ||
      valor === 0 ||
      valor === "0" ||
      valor === "nao" ||
      valor === "não" ||
      valor === "Nao" ||
      valor === "Não"
    ) {
      return "Não";
    }

    return "—";
  };

  const valorOuTraco = (valor: unknown) => {
    if (valor === null || valor === undefined || valor === "") return "—";
    return String(valor);
  };

  const formatarDataBR = (data?: string | null) => {
    if (!data) return "—";
    const base = data.slice(0, 10).split("-");
    if (base.length !== 3) return data;
    return `${base[2]}/${base[1]}/${base[0]}`;
  };

  const formatarDataHoraBR = (data?: string | null) => {
    if (!data) return "Pendente";
    const dt = new Date(data);
    if (Number.isNaN(dt.getTime())) return String(data);
    return dt.toLocaleString("pt-BR");
  };

  const formatarIdadePet = (pet: any) => {
    if (pet.idade !== null && pet.idade !== undefined && pet.idade !== "") {
      return `${pet.idade} ano(s)`;
    }

    if (pet.idade_meses !== null && pet.idade_meses !== undefined && pet.idade_meses !== "") {
      return `${pet.idade_meses} mes(es)`;
    }

    if (pet.data_nascimento) {
      const nasc = new Date(pet.data_nascimento);
      const hoje = new Date();
      let anos = hoje.getFullYear() - nasc.getFullYear();
      const m = hoje.getMonth() - nasc.getMonth();
      if (m < 0 || (m === 0 && hoje.getDate() < nasc.getDate())) anos--;
      if (anos >= 1) return `${anos} ano(s)`;
    }

    return "—";
  };

  const limparModalLote = () => {
  setLoteDataInicio("");
  setLoteDataFim("");
  setLoteHoraInicio("08:00");
  setLoteHoraFim("09:00");
  setLotePreco("");
};
  
  const abrirModalLote = () => {
    if (!servicoId) {
      setAvisoAgenda("Selecione um serviço antes de usar o ajuste em lote.");
      return;
    }

    limparModalLote();
    setModalLoteOpen(true);
  };

  const fecharModalLote = () => {
    setModalLoteOpen(false);
    limparModalLote();
  };

  const salvarPrecoLoteHorario = async () => {
      if (!servicoId || !loteDataInicio || !loteDataFim || !loteHoraInicio || !loteHoraFim || !lotePreco) {
      alert("Preencha datas, horários e preço.");
      return;
    }

    try {
      setSalvandoLote(true);

      const resp = await fetch("/disponibilidade/horaria/lote/ajustar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN": getCsrfToken(),
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          servico_id: servicoId,
          data_inicio: loteDataInicio,
          data_fim: loteDataFim,
          hora_inicio: loteHoraInicio,
          hora_fim: loteHoraFim,
          preco: parsePrecoBR(lotePreco),
        }),
      });

      if (!resp.ok) {
        const erroResp = await resp.json().catch(() => ({}));
        throw new Error(erroResp.message || "Erro ao salvar preço em lote.");
      }

      fecharModalLote();
      await fetchEventos();
      setAvisoAgenda("Preço salvo para os horários escolhidos.");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Erro ao salvar preço em lote.");
    } finally {
      setSalvandoLote(false);
    }
  };

const excluirPeriodoLoteHorario = async () => {
  if (!servicoId || !loteDataInicio || !loteDataFim || !loteHoraInicio || !loteHoraFim) {
    alert("Preencha datas e horários.");
    return;
  }

  if (!window.confirm("Deseja apagar esses horários?")) {
    return;
  }

  try {
    setSalvandoLote(true);

    const resp = await fetch("/disponibilidade/horaria/lote/excluir", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-TOKEN": getCsrfToken(),
        "X-Requested-With": "XMLHttpRequest",
      },
      credentials: "same-origin",
      body: JSON.stringify({
        servico_id: servicoId,
        data_inicio: loteDataInicio,
        data_fim: loteDataFim,
        hora_inicio: loteHoraInicio,
        hora_fim: loteHoraFim,
      }),
    });

    if (!resp.ok) {
      const erroResp = await resp.json().catch(() => ({}));
      throw new Error(erroResp.message || "Erro ao excluir horários.");
    }

    fecharModalLote();
    await fetchEventos();
    setAvisoAgenda("Horários apagados com sucesso.");
  } catch (error) {
    alert(error instanceof Error ? error.message : "Erro ao excluir horários.");
  } finally {
    setSalvandoLote(false);
  }
};
  const salvarPrecoLote = async () => {
    if (!servicoId || !loteDataInicio || !loteDataFim || !lotePreco) {
      alert("Preencha data inicial, data final e preço.");
      return;
    }

    try {
      setSalvandoLote(true);

      const resp = await fetch("/disponibilidade/diaria/lote/ajustar", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN": getCsrfToken(),
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          servico_id: servicoId,
          data_inicio: loteDataInicio,
          data_fim: loteDataFim,
          preco: parsePrecoBR(lotePreco),
        }),
      });

      if (!resp.ok) {
        const erroResp = await resp.json().catch(() => ({}));
        throw new Error(erroResp.message || "Erro ao salvar preço em lote.");
      }

      fecharModalLote();
      await fetchEventos();
      setAvisoAgenda("Preços atualizados em lote com sucesso.");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Erro ao salvar preço em lote.");
    } finally {
      setSalvandoLote(false);
    }
  };

  const excluirPeriodoLote = async () => {
    if (!servicoId || !loteDataInicio || !loteDataFim) {
      alert("Preencha data inicial e data final.");
      return;
    }

    if (!window.confirm("Tem certeza que deseja excluir esse período em lote?")) {
      return;
    }

    try {
      setSalvandoLote(true);

      const resp = await fetch("/disponibilidade/diaria/lote/excluir", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-TOKEN": getCsrfToken(),
          "X-Requested-With": "XMLHttpRequest",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          servico_id: servicoId,
          data_inicio: loteDataInicio,
          data_fim: loteDataFim,
        }),
      });

      if (!resp.ok) {
        const erroResp = await resp.json().catch(() => ({}));
        throw new Error(erroResp.message || "Erro ao excluir em lote.");
      }

      fecharModalLote();
      await fetchEventos();
      setAvisoAgenda("Período excluído em lote com sucesso.");
    } catch (error) {
      alert(error instanceof Error ? error.message : "Erro ao excluir em lote.");
    } finally {
      setSalvandoLote(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f6faf6] via-[#eef4ee] to-[#e8f0e8]">
        <Header />
        <div className="mx-auto max-w-7xl px-4 py-10">
          <div className="rounded-3xl border border-[#e7efe7] bg-white p-10 text-center shadow-sm">
            <div className="text-base font-semibold text-gray-700">Carregando agenda...</div>
            <div className="mt-2 text-sm text-gray-500">
              Aguarde enquanto preparamos sua estrutura.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDateClick = (info: DateClickArg) => {
  if (!podeSelecionarIntervalo(info.date)) {
    setAvisoAgenda("Não é possível selecionar datas ou horários passados.");
    return;
  }

  const data = extrairDataDeDate(info.date);

  if (ehHorario) {
    const inicio = extrairHoraDeDate(info.date);
    const fimDate = new Date(info.date.getTime() + 60 * 60000);
    const fim = extrairHoraDeDate(fimDate);

    setFormData({
      ...FORM_PADRAO,
      ...precosBanhoTosaAtuais(),
      data,
      horaInicio: inicio,
      horaFim: fim,
    });
  } else {
    setFormData({
      ...FORM_PADRAO,
      ...precosBanhoTosaAtuais(),
      data,
      horaInicio: "08:00",
      horaFim: "09:00",
    });
  }

  setModalOpen(true);
};

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f6faf6] via-[#eef4ee] to-[#e8f0e8]">
      <Header />

      <div className="mx-auto max-w-7xl px-4 py-6 lg:px-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[290px_minmax(0,1fr)]">
          <ProfileSidebarAnfitriao
            userName={window.__AUTH_USER__?.name || "Anfitrião"}
            bio="Gerencie sua agenda, disponibilidade e reservas."
            avatarUrl={getAvatarUrl(window.__AUTH_USER__)}
            perfilPct={80}
            cidadeEstado="Maringá - PR"
            isMv={false}
            abaAtiva="dashboard"
            links={{
              perfil: "/anfitriao/perfil",
              mensagens: "/chat",
              agenda: "/anfitriao/agenda",
              reservas: "/anfitriao/reservas",
              manual: "/manual-do-anfitriao",
            }}
            onNavigate={(aba) => {
              const destino =
                aba === "perfil"
                  ? "/anfitriao/dashboard?aba=perfil"
                  : aba === "financeiro"
                  ? "/anfitriao/dashboard?aba=financeiro"
                  : aba === "avaliacoes"
                  ? "/anfitriao/dashboard?aba=avaliacoes"
                  : "/anfitriao/dashboard";

              window.location.href = destino;
            }}
          />

          <div className="space-y-5">
            <Card className="overflow-hidden rounded-3xl border-0 bg-white shadow-sm">
              <div className="bg-gradient-to-r from-[#1f7a4d] to-[#2f9e68] px-6 py-7 text-white">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <h1 className="text-2xl font-bold tracking-tight">Minha Agenda</h1>
                    <p className="mt-1 text-sm text-white/90">
                      Gerencie datas, horários, preços especiais e reservas em um só lugar.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white/15 px-4 py-3 text-sm backdrop-blur-sm">
                    <div className="font-semibold">Serviço atual</div>
                    <div className="text-white/90">
                      {servicoAtual?.nome || "Selecione um serviço"}
                    </div>
                  </div>
                </div>
              </div>

              <CardContent className="p-5 md:p-6">
                {erro && (
                  <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {erro}
                  </div>
                )}

                <div className="mb-5 rounded-2xl border border-[#e7efe7] bg-[#f8fbf8] p-4">
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(240px,320px)_1fr] lg:items-end">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Serviço
                      </label>
                      <select
                        value={servicoId}
                        onChange={(e) => setServicoId(e.target.value)}
                        className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm shadow-sm outline-none transition focus:border-green-500 focus:ring-2 focus:ring-green-200"
                      >
                        {servicos.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                        
                    <div className="flex flex-wrap gap-2 lg:justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() =>
                          window.location.href = `/anfitriao/dashboard?aba=perfil&secao=servicos&servico_id=${servicoId || ""}`
                        }
                        className="rounded-full"
                      >
                        Editar serviço
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={abrirModalLote}
                        className="rounded-full"
                      >
                        📅 Ajuste em lote
                      </Button>
                    </div>    
                  </div>
                </div>

                <div className="mb-5 flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-2 text-xs font-medium text-blue-700 ring-1 ring-blue-100">
                    <span className="h-2.5 w-2.5 rounded-full bg-blue-500" />
                    Reserva pendente
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-green-50 px-3 py-2 text-xs font-medium text-green-700 ring-1 ring-green-100">
                    <span className="h-2.5 w-2.5 rounded-full bg-green-500" />
                    Reserva aceita
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-red-50 px-3 py-2 text-xs font-medium text-red-700 ring-1 ring-red-100">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-500" />
                    Indisponível
                  </span>
                  <span className="inline-flex items-center gap-2 rounded-full bg-yellow-50 px-3 py-2 text-xs font-medium text-yellow-700 ring-1 ring-yellow-100">
                    <span className="h-2.5 w-2.5 rounded-full bg-yellow-400" />
                    Preço especial
                  </span>
                </div>

                <div className="rounded-3xl border border-[#e8eee8] bg-white p-3 shadow-inner sm:p-4">
                  {loadingEventos && (
                    <div className="mb-3 animate-pulse text-center text-xs text-gray-400">
                      Sincronizando agenda...
                    </div>
                  )}

                  {avisoAgenda && (
                    <div className="mb-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                      {avisoAgenda}
                    </div>
                  )}
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                    locale={ptBrLocale}
                    key={`${servicoId}-${ehHorario ? "timeGridDay" : "dayGridMonth"}`}
                    initialView={ehHorario ? "timeGridDay" : "dayGridMonth"}
                    events={eventos}
                    selectable
                    select={handleSelect}
                    dateClick={handleDateClick}
                    selectLongPressDelay={isMobile ? 450 : 300}
                    longPressDelay={isMobile ? 450 : 300}
                    eventClick={handleEventClick}
                    height="auto"
                    slotMinTime="05:00:00"
                    slotMaxTime="23:30:00"
                    slotDuration="00:30:00"
                    slotLabelInterval="00:30:00"
                    allDaySlot={false}
                    expandRows={true}
                    nowIndicator={true}
                    scrollTime="05:00:00"
                    headerToolbar={{
                      left: isMobile ? "prev,next" : "prev,next today",
                      center: "title",
                      right: ehHorario ? "timeGridDay,timeGridWeek,dayGridMonth" : "dayGridMonth",
                    }}
                    selectAllow={(info) => {
                      if (!podeSelecionarIntervalo(info.start)) return false;

                      if (ehHorario) {
                        const hora = info.start.getHours();
                        if (hora < 5 || hora > 22) return false;
                      }

                      return true;
                    }}
                    eventContent={(arg) => (
                      <div className="overflow-hidden rounded-md px-1 py-0.5">
                        <div className="truncate text-[10px] font-semibold sm:text-xs">
                          {arg.event.title}
                        </div>
                        {arg.event.extendedProps?.preco && (
                          <div className="text-[9px] opacity-80 sm:text-[10px]">
                            R$ {arg.event.extendedProps.preco}
                          </div>
                        )}
                      </div>
                    )}

                    eventDidMount={(info) => {
                      const tipo = info.event.extendedProps?.tipo;
                      const status = info.event.extendedProps?.status;

                      if (tipo === "disponibilidade" && status === "disponivel") {
                        info.el.style.pointerEvents = "auto";
                      }
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

            {modalLoteOpen && (
              <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
                <Card className="w-full max-w-md rounded-3xl border-0 shadow-2xl">
                  <CardContent className="p-6">
                    <div className="mb-5">
                      <h3 className="text-xl font-bold text-gray-900">
                        {ehHorario ? "Ajuste em lote - horários" : "Ajuste em lote - dias"}
                      </h3>

                      <p className="mt-1 text-sm text-gray-500">
                        {ehHorario
                          ? "Defina o mesmo preço para vários horários ou apague vários horários."
                          : "Defina o mesmo preço para vários dias ou apague vários dias."}
                      </p>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Data inicial
                        </label>
                        <input
                          type="date"
                          className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                          value={loteDataInicio}
                          onChange={(e) => setLoteDataInicio(e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Data final
                        </label>
                        <input
                          type="date"
                          className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                          value={loteDataFim}
                          onChange={(e) => setLoteDataFim(e.target.value)}
                        />
                      </div>

                      {ehHorario && (
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Hora inicial
                            </label>
                            <input
                              type="time"
                              className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                              value={loteHoraInicio}
                              onChange={(e) => setLoteHoraInicio(e.target.value)}
                            />
                          </div>

                          <div>
                            <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Hora final
                            </label>
                            <input
                              type="time"
                              className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                              value={loteHoraFim}
                              onChange={(e) => setLoteHoraFim(e.target.value)}
                            />
                          </div>
                        </div>
                      )}

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {ehHorario ? "Novo preço do horário" : "Novo preço diário"}
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                            value={lotePreco}
                            onChange={(e) => setLotePreco(e.target.value)}
                          />

                          <button
                            type="button"
                            onClick={() => setCalculadoraLoteAberta(true)}
                            className="whitespace-nowrap rounded-2xl border border-gray-200 px-4 py-3 text-sm hover:bg-gray-50"
                          >
                            🧮 Calcular
                          </button>
                        </div>

                        <CalculadoraTaxaModal
                          aberto={calculadoraLoteAberta}
                          onClose={() => setCalculadoraLoteAberta(false)}
                          comissaoPercentual={25}
                          onAplicar={(valorCobrar) => {
                            setLotePreco(valorCobrar);
                          }}
                        />
                      </div>

                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        <Button
                          type="button"
                          onClick={ehHorario ? salvarPrecoLoteHorario : salvarPrecoLote}
                          disabled={salvandoLote}
                          className="rounded-2xl bg-[#111827] text-white"
                        >
                          {salvandoLote ? "Salvando..." : "Salvar preço"}
                        </Button>

                        <Button
                          type="button"
                          variant="destructive"
                          onClick={ehHorario ? excluirPeriodoLoteHorario : excluirPeriodoLote}
                          disabled={salvandoLote}
                          className="rounded-2xl"
                        >
                          {salvandoLote ? "Excluindo..." : "Excluir"}
                        </Button>
                      </div>

                      <Button
                        type="button"
                        variant="ghost"
                        onClick={fecharModalLote}
                        className="w-full rounded-2xl"
                      >
                        Cancelar
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

      {modalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-md rounded-3xl border-0 shadow-2xl">
            <CardContent className="p-6">
              <div className="mb-5">
                <h3 className="text-xl font-bold text-gray-900">
                  {formData.id ? "Editar disponibilidade" : "Nova disponibilidade"}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Ajuste preço, horário ou bloqueie esta data.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Data
                  </label>
                  <input
                    ref={primeiroCampoModalRef}
                    type="date"
                    min={hojeStr}
                    className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                    value={formData.data}
                    onChange={(e) => setFormData({ ...formData, data: e.target.value })}
                  />
                </div>

                {ehHorario && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Início
                      </label>
                      <input
                        type="time"
                        min={formData.data === hojeStr ? getHoraAtualStr() : undefined}
                        className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                        value={formData.horaInicio}
                        onChange={(e) =>
                          setFormData({ ...formData, horaInicio: e.target.value })
                        }
                      />
                    </div>

                    <div>
                      <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Fim
                      </label>
                      <input
                        type="time"
                        min={formData.horaInicio}
                        className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                        value={formData.horaFim}
                        onChange={(e) =>
                          setFormData({ ...formData, horaFim: e.target.value })
                        }
                      />
                    </div>
                  </div>
                )}

                {!ehBanhoTosa && (
                  <div>
                    <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Preço
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={formData.preco}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            preco: e.target.value,
                          }))
                        }
                        className="w-full rounded-2xl border border-gray-200 px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                      />

                      <CalculadoraTaxaModal
                        aberto={calculadoraAberta}
                        onClose={() => setCalculadoraAberta(false)}
                        comissaoPercentual={25}
                        onAplicar={(valorCobrar) => {
                          setFormData((prev) => ({
                            ...prev,
                            preco: valorCobrar,
                          }));
                        }}
                      />

                      {!formData.indisponivel && (
                        <button
                          type="button"
                          onClick={() => setCalculadoraAberta(true)}
                          className="whitespace-nowrap rounded-2xl border border-gray-200 px-4 py-3 text-sm hover:bg-gray-50"
                        >
                          🧮 Calcular
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {ehBanhoTosa && (
                  <div className="rounded-2xl border border-green-100 bg-green-50 p-4">
                    <div className="mb-3">
                      <p className="text-sm font-semibold text-gray-900">
                        Preços padrão
                      </p>
                      <p className="text-xs text-gray-600">
                        Esses valores serão usados na reserva conforme a escolha do tutor.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Banho
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formData.precoBanho}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              precoBanho: e.target.value,
                            }))
                          }
                          className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Tosa
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formData.precoTosa}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              precoTosa: e.target.value,
                            }))
                          }
                          className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                        />
                      </div>

                      <div>
                        <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Banho e Tosa
                        </label>
                        <input
                          type="text"
                          inputMode="decimal"
                          value={formData.precoBanhoTosa}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              precoBanhoTosa: e.target.value,
                            }))
                          }
                          className="w-full rounded-2xl border border-gray-200 bg-white px-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-200"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <label className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={formData.indisponivel}
                    onChange={(e) =>
                      setFormData({ ...formData, indisponivel: e.target.checked })
                    }
                  />
                  <span className="text-sm text-gray-700">Marcar como indisponível</span>
                </label>

                <div className="flex flex-col-reverse gap-2 pt-2 sm:flex-row">
                  <Button
                    variant="ghost"
                    onClick={() => setModalOpen(false)}
                    className="flex-1 rounded-2xl"
                    type="button"
                  >
                    Cancelar
                  </Button>

                  {formData.id && (
                    <Button
                      variant="destructive"
                      onClick={handleDelete}
                      className="rounded-2xl"
                      type="button"
                    >
                      Excluir
                    </Button>
                  )}

                  <Button
                    onClick={handleSave}
                    disabled={salvando}
                    className="flex-1 rounded-2xl bg-[#111827] text-white"
                    type="button"
                  >
                    {salvando ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

     {modalReservaOpen && !!reservaSelecionada && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <Card className="w-full max-w-2xl rounded-3xl border-0 shadow-2xl">
            <CardContent className="p-6">
              <div className="mb-5">
                <h3 className="text-xl font-bold text-gray-900">
                  Reserva de {reservaSelecionada.tutor}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Analise os dados antes de aceitar ou recusar.
                </p>
              </div>

              <div className="mb-4 space-y-2 rounded-2xl border border-gray-200 bg-gray-50 p-4 text-sm">
                <p>
                  <strong>Status:</strong> {reservaSelecionada.status}
                </p>

                <p>
                  <strong>Período:</strong> {formatarDataBR(reservaSelecionada.dataInicio)}
                  {reservaSelecionada.dataFim &&
                  reservaSelecionada.dataFim !== reservaSelecionada.dataInicio
                    ? ` até ${formatarDataBR(reservaSelecionada.dataFim)}`
                    : ""}
                </p>

                {(reservaSelecionada.horaInicio || reservaSelecionada.horaFim) && (
                  <p>
                   <strong>Horário:</strong> {formatarHoraSimples(reservaSelecionada.horaInicio)} às{" "}
                    {formatarHoraSimples(reservaSelecionada.horaFim)}
                  </p>
                )}

                <p>
                  <strong>Telefone:</strong> {formatarTelefoneBR(reservaSelecionada.telefoneTutor)}
                </p>

                <p>
                  <strong>Valor líquido a receber:</strong> R$ {reservaSelecionada.valorLiquido || "0,00"}
                </p>

                <p>
                  <strong>Check-in:</strong>{" "}
                  {formatarDataHoraBR(reservaSelecionada.checkinRealizadoEm)}
                </p>

                <p>
                  <strong>Check-out:</strong>{" "}
                  {formatarDataHoraBR(reservaSelecionada.checkoutRealizadoEm)}
                </p>
              </div>

              <div className="mb-4">
                <h4 className="mb-3 text-sm font-semibold text-gray-700">
                  Pets da reserva
                </h4>

                <div className="max-h-[28rem] space-y-4 overflow-y-auto pr-1">
                  {reservaSelecionada.pets.map((pet, i) => {
                    const expandido = petExpandido === i;
                    const resumoSaude = calcularResumoSaudePet(pet);
                    
                    return (
                      <div
                        key={i}
                        className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm"
                      >
                        <div className="flex items-start gap-4 p-4">
                          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-gray-100">
                            {pet.foto ? (
                              <img
                                src={pet.foto}
                                alt={pet.nome}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <span className="text-xs text-gray-400">Sem foto</span>
                            )}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                              <div>
                                <div className="text-base font-semibold text-gray-900">
                                  {pet.nome}
                                </div>

                                <div className="mt-1 text-sm text-gray-600">
                                  {valorOuTraco(pet.tipo_pet)} • {valorOuTraco(pet.raca)}
                                </div>
                              </div>

                              <button
                                type="button"
                                onClick={() => setPetExpandido(expandido ? null : i)}
                                className="rounded-2xl border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                              >
                                {expandido ? "Ocultar ficha" : "Ver ficha completa"}
                              </button>
                            </div>

                            <div className="mt-4 grid grid-cols-2 gap-2 text-sm text-gray-700 md:grid-cols-4">
                              <div className="rounded-2xl bg-gray-50 px-3 py-2">
                                <strong>Sexo:</strong> {valorOuTraco(pet.sexo)}
                              </div>
                              <div className="rounded-2xl bg-gray-50 px-3 py-2">
                                <strong>Peso:</strong> {formatarPeso(pet.peso)}
                              </div>
                              <div className="rounded-2xl bg-gray-50 px-3 py-2">
                                <strong>Idade:</strong> {formatarIdadePet(pet)}
                              </div>
                              <div className="rounded-2xl bg-gray-50 px-3 py-2">
                                <strong>Castrado:</strong> {textoSimNao(pet.castrado)}
                              </div>
                            </div>
                          </div>
                        </div>

                        {expandido && (
                          <div className="border-t border-gray-100 bg-gray-50 p-4">
                            <div className="grid gap-4 md:grid-cols-2">
                              <div className="rounded-2xl bg-white p-4 shadow-sm">
                                <h5 className="mb-3 text-sm font-semibold text-gray-800">
                                  Saúde
                                </h5>
                                <div className="space-y-2 text-sm text-gray-700">
                                  <div><strong>Microchipado:</strong> {textoSimNao(pet.microchipado)}</div>
                                  <div><strong>Toma medicação:</strong> {textoSimNao(pet.toma_medicacao)}</div>
                                  <div><strong>Medicação:</strong> {valorOuTraco(pet.medicacao)}</div>
                                  <div><strong>Obs. medicações:</strong> {valorOuTraco(pet.obs_medicacoes)}</div>
                                  <div><strong>Tem alergia:</strong> {textoSimNao(pet.tem_alergia)}</div>
                                  <div><strong>Alergias:</strong> {valorOuTraco(pet.alergias)}</div>
                                  <div><strong>Doenças:</strong> {valorOuTraco(pet.doencas)}</div>
                                  <div><strong>Possui deficiência:</strong> {textoSimNao(pet.possui_deficiencia)}</div>
                                  <div><strong>Tipo de deficiência:</strong> {valorOuTraco(pet.tipo_deficiencia)}</div>
                                </div>
                              </div>
                              <div className="rounded-3xl border border-gray-200 bg-white p-4">
                                <h4 className="mb-3 text-base font-semibold text-gray-900">Vacinas e parasitários</h4>

                                <div className="space-y-2 text-sm text-gray-700">
                                  <p>
                                    <span className="font-semibold">Status geral:</span> {resumoSaude.statusGeral}
                                  </p>

                                  <p>
                                    <span className="font-semibold">Vacinas:</span>{" "}
                                    Atrasadas: {resumoSaude.vacinas.atrasadas} •
                                    Próximas: {resumoSaude.vacinas.proximas} •
                                    Em dia: {resumoSaude.vacinas.emDia}
                                  </p>

                                  <p>
                                    <span className="font-semibold">Vermífugos / antiparasitários:</span>{" "}
                                    Atrasados: {resumoSaude.parasitas.atrasados} •
                                    Próximos: {resumoSaude.parasitas.proximos} •
                                    Em dia: {resumoSaude.parasitas.emDia}
                                  </p>

                                  <p>
                                    <span className="font-semibold">Vacinas em dia:</span>{" "}
                                    {resumoSaude.vacinas.atrasadas === 0 &&
                                    (resumoSaude.vacinas.proximas > 0 || resumoSaude.vacinas.emDia > 0)
                                      ? "Sim"
                                      : "Não"}
                                  </p>

                                  <p>
                                    <span className="font-semibold">Vermifugação em dia:</span>{" "}
                                    {resumoSaude.parasitas.atrasados === 0 &&
                                    (resumoSaude.parasitas.proximos > 0 || resumoSaude.parasitas.emDia > 0)
                                      ? "Sim"
                                      : "Não"}
                                  </p>
                                </div>
                              </div>
                              <div className="rounded-2xl bg-white p-4 shadow-sm">
                                <h5 className="mb-3 text-sm font-semibold text-gray-800">
                                  Comportamento
                                </h5>
                                <div className="space-y-2 text-sm text-gray-700">
                                  <div><strong>Sociável com pets:</strong> {textoSimNao(pet.sociavel_pets)}</div>
                                  <div><strong>Sociável com crianças:</strong> {textoSimNao(pet.sociavel_criancas)}</div>
                                  <div><strong>Aceita outros pets:</strong> {textoSimNao(pet.aceita_outros_pets)}</div>
                                  <div><strong>Agressivo:</strong> {textoSimNao(pet.agressivo)}</div>
                                  <div><strong>Medo de chuva:</strong> {textoSimNao(pet.medo_chuva)}</div>
                                  <div><strong>Medo de trovão/fogos:</strong> {textoSimNao(pet.medo_trovao_fogos)}</div>
                                  <div><strong>Dorme dentro:</strong> {textoSimNao(pet.dorme_dentro)}</div>
                                  <div><strong>Precisa supervisão constante:</strong> {textoSimNao(pet.precisa_supervisao_constante)}</div>
                                  <div><strong>Precisa espaço cercado:</strong> {textoSimNao(pet.precisa_espaco_cercado)}</div>
                                </div>
                              </div>

                              <div className="rounded-2xl bg-white p-4 shadow-sm">
                                <h5 className="mb-3 text-sm font-semibold text-gray-800">
                                  Rotina e alimentação
                                </h5>
                                <div className="space-y-2 text-sm text-gray-700">
                                  <div><strong>Alimentação especial:</strong> {textoSimNao(pet.alimentacao_especial)}</div>
                                  <div><strong>Descrição da alimentação:</strong> {valorOuTraco(pet.descricao_alimentacao)}</div>
                                  <div><strong>Horário da refeição:</strong> {valorOuTraco(pet.horario_refeicao)}</div>
                                  <div><strong>Horário de passeio:</strong> {valorOuTraco(pet.horario_passeio)}</div>
                                  <div><strong>Objetos essenciais:</strong> {valorOuTraco(pet.objetos_essenciais)}</div>
                                  <div><strong>Observações:</strong> {valorOuTraco(pet.observacoes)}</div>
                                </div>
                              </div>

                              <div className="rounded-2xl bg-white p-4 shadow-sm">
                                <h5 className="mb-3 text-sm font-semibold text-gray-800">
                                  Emergência
                                </h5>
                                <div className="space-y-2 text-sm text-gray-700">
                                  <div><strong>Emergência:</strong> {valorOuTraco(pet.emergencia)}</div>
                                  <div><strong>Contato médico/vet:</strong> {valorOuTraco(pet.contato_medico_vet)}</div>
                                  <div><strong>Autoriza veterinário:</strong> {textoSimNao(pet.autoriza_vet)}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {reservaSelecionada.chatUrl && (
                <a
                  href={reservaSelecionada.chatUrl}
                  className="mb-3 block w-full rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-center text-sm font-medium text-blue-700"
                >
                  Falar com o tutor no chat
                </a>
              )}

              {reservaSelecionada.status === "pendente" && (
                <div className="mt-6 flex gap-3">
                  <Button
                    onClick={() => aceitarReserva(reservaSelecionada.id)}
                    disabled={acaoReservaCarregando !== null}
                    className="flex-1 rounded-2xl bg-green-600 text-white disabled:opacity-50"
                    type="button"
                  >
                    {acaoReservaCarregando === "aceitar" ? "Aceitando..." : "Aceitar"}
                  </Button>

                  <Button
                    onClick={() => recusarReserva(reservaSelecionada.id)}
                    disabled={acaoReservaCarregando !== null}
                    className="flex-1 rounded-2xl bg-red-600 text-white disabled:opacity-50"
                    type="button"
                  >
                    {acaoReservaCarregando === "recusar" ? "Recusando..." : "Recusar"}
                  </Button>
                </div>
              )}

              {reservaSelecionada.status === "confirmada" &&
                !reservaSelecionada.checkinRealizadoEm && (
                  <div className="mt-6">
                    <Button
                      onClick={() => realizarCheckin(reservaSelecionada.id)}
                      disabled={acaoReservaCarregando !== null}
                      className="w-full rounded-2xl bg-amber-500 text-white disabled:opacity-50"
                      type="button"
                    >
                      {acaoReservaCarregando === "checkin"
                        ? "Realizando check-in..."
                        : "Realizar check-in"}
                    </Button>
                  </div>
                )}

              {reservaSelecionada.checkinRealizadoEm &&
                !reservaSelecionada.checkoutRealizadoEm && (
                  <div className="mt-3">
                    <Button
                      onClick={() => realizarCheckout(reservaSelecionada.id)}
                      disabled={acaoReservaCarregando !== null}
                      className="w-full rounded-2xl bg-sky-600 text-white disabled:opacity-50"
                      type="button"
                    >
                      {acaoReservaCarregando === "checkout"
                        ? "Realizando check-out..."
                        : "Realizar check-out"}
                    </Button>
                  </div>
                )}

              <Button
                onClick={() => setModalReservaOpen(false)}
                className="mt-3 w-full rounded-2xl bg-[#111827] text-white"
                type="button"
              >
                Fechar
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      <style>{`
        .fc .fc-bg-event {
          background: #b4b4b4 !important;
          background-color: #b4b4b4 !important;
          opacity: 1 !important;
        }

        .fc .fc-bg-event.bg-passado-hoje {
          background: #b4b4b4 !important;
          opacity: 1 !important;
          pointer-events: auto !important;
          cursor: not-allowed !important;
          z-index: 2 !important;
        }

        .fc .fc-bg-event.bg-passado-dia {
          background: #b4b4b4 !important;
          opacity: 1 !important;
          cursor: not-allowed !important;
          z-index: 2 !important;
        }

        .fc .fc-timegrid-col-events {
          z-index: 3 !important;
        }

        .fc {
          --fc-border-color: #94a3b8;
          --fc-button-bg-color: #ffffff;
          --fc-button-text-color: #374151;
          --fc-button-hover-bg-color: #f8fafc;
          --fc-button-active-bg-color: #e2e8f0;
          --fc-page-bg-color: #ffffff;
          --fc-today-bg-color: rgba(34, 197, 94, 0.06);
        }

        .fc-theme-standard td,
        .fc-theme-standard th {
          border-color: #cbd5e1 !important;
        }

        .fc .fc-scrollgrid,
        .fc .fc-timegrid-slot,
        .fc .fc-timegrid-axis,
        .fc .fc-daygrid-body,
        .fc .fc-daygrid-body-natural,
        .fc .fc-timegrid-divider,
        .fc .fc-col-header,
        .fc .fc-scrollgrid-section > * {
          border-color: #cbd5e1 !important;
        }

        .fc .fc-timegrid-slot-minor {
          border-top-color: #94a3b8 !important;
        }

        .fc .fc-toolbar {
          gap: 10px;
          margin-bottom: 1rem !important;
        }

        .fc .fc-toolbar-title {
          font-size: 1.15rem !important;
          font-weight: 700;
          color: #111827;
        }

        .fc .fc-button {
          border: 1px solid #e5e7eb !important;
          border-radius: 999px !important;
          text-transform: capitalize !important;
          font-size: 0.8rem !important;
          font-weight: 600 !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04) !important;
          padding: 0.45rem 0.85rem !important;
        }

        .fc .fc-button:hover {
          border-color: #111827 !important;
        }

        .fc .fc-daygrid-day-number {
          font-size: 0.82rem;
          color: #6b7280;
          padding: 8px !important;
        }

        .fc .fc-event {
          border: 0 !important;
          border-radius: 10px !important;
          padding: 2px !important;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
          cursor: pointer !important;
          transition: transform 0.15s ease, box-shadow 0.15s ease;
        }

        .fc .fc-event:hover {
          transform: scale(1.01);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
          z-index: 50 !important;
        }

        .fc .fc-col-header-cell-cushion {
          padding: 10px 4px !important;
          font-size: 0.75rem;
          font-weight: 700;
          color: #6b7280;
        }

        .fc .fc-timegrid-slot {
          height: 2.8rem !important;
        }

        .fc .fc-timegrid-axis-cushion,
        .fc .fc-timegrid-slot-label-cushion {
          font-size: 0.75rem;
          color: #2e3136;
        }

        .fc .fc-timegrid-now-indicator-line {
          border-color: #ef4444 !important;
          border-width: 2px 0 0 !important;
        }

        .fc .fc-timegrid-now-indicator-arrow {
          border-left-color: #ef4444 !important;
        }

        .fc .fc-timegrid-event-harness {
          transition: right 0.15s ease;
        }

        .fc .fc-timegrid-event {
          margin-right: 0 !important;
        }
        @media (max-width: 768px) {
          .fc .fc-toolbar {
            flex-direction: column;
            align-items: stretch !important;
          }

          .fc .fc-toolbar-title {
            text-align: center;
            font-size: 1rem !important;
          }
        }
      `}</style>
    </div>
  );
}
